<template>
  <div>
    <page-header
      :page="pageConfig"
      :actions="pageActionsConfig"
      :has-return="true"
    />
    <progress-filter
      :academic-years="academicYears"
      :default-value="lastAcademicYear"
      @on-change-academic-year="onChangeAcademicYearHandler"
    />
    <section-title
      class="mb-3 pl-1 ml-1 section-title__extended"
      icon="fas fa-clipboard-list-check"
      title="Resumen de Información General"
      title-class="font-16"
    />
    <div class="row">
      <div class="col col-sm col-12 ml-2">
        <general-info-summary
          title="Regular"
          total-title="Total de Casos"
          pending-title="Casos Pendientes"
          completed-title="Por ciento de Casos Completados"
          :total-count="regularSummary.totalCount"
          :pending-count="regularSummary.pendingCount"
          :completed-count="regularSummary.completedPercentage"
        />
      </div>
      <div class="col col-sm col-12 ml-2 mt-2 mt-sm-2 mt-md-0">
        <general-info-summary
          title="Educativo"
          total-title="Total de Casos"
          pending-title="Casos Pendientes"
          completed-title="Por ciento de Casos Completados"
          :total-count="educationalSummary.totalCount"
          :pending-count="educationalSummary.pendingCount"
          :completed-count="educationalSummary.completedPercentage"
        />
      </div>
      <div class="col col-sm col-12 ml-2 mt-2 mt-sm-2 mt-md-0">
        <general-info-summary
          title="Terapia"
          total-title="Total de Casos"
          pending-title="Casos Pendientes"
          completed-title="Por ciento de Casos Completados"
          :total-count="therapySummary.totalCount"
          :pending-count="therapySummary.pendingCount"
          :completed-count="therapySummary.completedPercentage"
        />
      </div>
    </div>
    <div class="ml-2 mt-3">
      <summary-collapse
        :is-opened="true"
        :action-summary="actionSummary"
        :filter-items="categories"
        @on-change-filter-item="onChangeFilterItemHandler"
        title="Detalle de Resumen Actual"
      >
        <progress-info-summary :items="currentProgressSummary" />
      </summary-collapse>
    </div>
    <div class="mt-4 ml-2">
      <section-title has-line title="Historial de Informes de Progreso" />
      <progress-info-history
        :items="progressHistory"
        :action-items="actionItems"
        @on-refresh="onRefreshHandler"
        @on-close-ip="onCloseIpHandler"
        @on-update-ip="onUpdateIpHandler"
        @on-open-detail="onOpenDetailHandler"
      />
    </div>
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import SectionTitle from 'SectionTitle';
import {
  ProgressFilter,
  GeneralInfoSummary,
  SummaryCollapse,
  ProgressInfoSummary,
  ProgressInfoHistory,
} from '../shared/components';
import Service from '@/services/api/progress.api';
import DocumentServices from '@/modules/report/document';
import stipulationType from '@/utils/constants/stipulationType.js';
import categoryTypeList from '@/utils/constants/stipluation54CategoryTypeList';

export default {
  components: {
    PageHeader,
    SectionTitle,
    ProgressFilter,
    GeneralInfoSummary,
    SummaryCollapse,
    ProgressInfoSummary,
    ProgressInfoHistory,
  },
  data: () => ({
    academicYears: [],
    selectedAcademicYear: null,
    selectedCategoryType: categoryTypeList[0],
    regularSummary: {
      totalCount: 0,
      pendingCount: 0,
      completedPercentage: 0,
    },
    educationalSummary: {
      totalCount: 0,
      pendingCount: 0,
      completedPercentage: 0,
    },
    therapySummary: {
      totalCount: 0,
      pendingCount: 0,
      completedPercentage: 0,
    },
    progressHistory: [],
    currentProgressSummary: [],
  }),
  computed: {
    stipulationType() {
      return stipulationType.ST54;
    },
    categories() {
      return categoryTypeList;
    },
    pageConfig() {
      return {
        title: 'Informes de Progreso: Estipulación 54',
        icon: 'fas fa-calculator',
        routes: ['Inicio', 'Informes de Progreso', 'Estipulación 54'],
      };
    },
    pageActionsConfig() {
      return [
        {
          text: 'Actualizar IP',
          icon: 'fas fa-sync-alt pr-2',
          variant: 'success',
          visible: true,
          click: () => this.onUpdateProgressReportHandler(),
        },
      ];
    },
    actionSummary() {
      return () => {
        return [
          {
            label: '',
            icon: 'fas fa-print text-green',
            class: 'pr-1',
            visible: true,
            click: () => this.onDownloadSummaryHandler(),
          },
        ];
      };
    },
    actionItems() {
      return (item) => {
        return [
          {
            label: '',
            icon: 'fas fa-print text-green',
            class: 'pr-1',
            visible: true,
            click: () => this.onDownloadHandler(item),
          },
        ];
      };
    },
    isLastIpClosed() {
      if (!this.progressHistory.length) return true;
      return this.progressHistory[0].isClosed;
    },
    lastAcademicYear() {
      if (!this.academicYears?.length) {
        return null;
      }

      return this.academicYears.reduce((max, academicYear) =>
        max.id > academicYear.id ? max : academicYear
      );
    },
    isLastAcademicYearSelected() {
      if (!this.academicYears?.length) {
        return false;
      }
      return this.lastAcademicYear?.id == this.selectedAcademicYear?.id;
    },
  },
  async mounted() {
    await this.getInit();
  },
  methods: {
    async getInit() {
      await this.getAvailableAcademicYears();
      if (this.academicYears?.length) {
        this.selectedAcademicYear = this.lastAcademicYear;
        await this.getDetail();
      }
    },
    async getAvailableAcademicYears() {
      this.academicYears = (
        await Service.getAcademicYears(this.stipulationType)
      ).data;
    },
    async getDetail() {
      const response = (
        await Service.getDetail({
          stipulationType: this.stipulationType,
          academicYearId: this.selectedAcademicYear.id,
          filter: this.selectedCategoryType.id,
        })
      ).data;
      if (response) {
        this.regularSummary = response.summaries[0];
        this.educationalSummary = response.summaries[1];
        this.therapySummary = response.summaries[2];
        this.currentProgressSummary = response.currentSummaryDetail;
        this.progressHistory = response.progressHistory;
      }
    },
    async onRefreshHandler() {
      await this.getDetail();
    },
    onChangeAcademicYearHandler(academicYear) {
      this.selectedAcademicYear = academicYear;
      this.getDetail();
    },
    onChangeFilterItemHandler(categoryType) {
      this.selectedCategoryType = categoryType;
      this.getDetail();
    },
    async onUpdateIpHandler() {
      Service.updateIp(
        this.stipulationType,
        this.selectedAcademicYear.id,
        true
      ).then((res) => {
        if (res.data) {
          this.ShowToast(
            'Proceso Exitoso',
            `El IP ha sido actualizado exitosamente.`,
            'success'
          );
          this.onRefreshHandler();
        } else {
          this.ShowToast(
            'Alerta',
            'No cuenta con datos para actualizar en sistema.',
            'error'
          );
        }
      });
    },
    async onUpdateProgressReportHandler() {
      if (!this.isLastIpClosed) {
        this.ShowToast(
          'Alerta',
          'Para crear una nueva actualización es necesario cerrar el último IP.',
          'error'
        );
      } else {
        this.ShowCancelModal(
          'Alerta',
          '¿Está seguro que desea actualizar los datos y generar un IP?',
          'Sí',
          'No'
        ).then((res) => {
          if (res) {
            Service.updateIp(
              this.stipulationType,
              this.selectedAcademicYear.id,
              false
            ).then((res) => {
              if (res.data) {
                this.ShowToast(
                  'Proceso Exitoso',
                  'El IP ha sido actualizado exitosamente.',
                  'success'
                );
                this.onRefreshHandler();
              } else {
                this.ShowToast(
                  'Alerta',
                  'No es posible generar un IP, ya que no cuenta con datos para actualizar en sistema.',
                  'error'
                );
              }
            });
          }
        });
      }
    },
    async onCloseIpHandler({ summaryId }) {
      if (summaryId) {
        this.ShowCancelModal(
          'Alerta',
          '¿Está seguro que desea congelar los datos y cerrar un IP?',
          'Sí',
          'No'
        ).then(async (res) => {
          if (res) {
            const result = (
              await Service.closeIp(this.stipulationType, summaryId)
            ).data;
            if (result) {
              this.onRefreshHandler();
              this.ShowToast(
                'Proceso Exitoso',
                'El IP ha sido cerrado exitosamente.',
                'success'
              );
            }
          }
        });
      }
    },
    async onDownloadSummaryHandler() {
      if (this.selectedAcademicYear) {
        var academicYearId = this.selectedAcademicYear.id;
        this.onDownloadHandler({ academicYearId, ipSequence: 0 });
      }
    },
    async onDownloadHandler(item) {
      await Service.downloadIpStipulation({
        stipulationType: this.stipulationType,
        ipSequence: item.ipSequence,
        academicYearId: item.academicYearId,
      })
        .then((res) => DocumentServices.Download(res.data))
        .catch((err) => {
          this.ShowToast('', err.response?.data?.message, 'error');
        });
    },
    async onOpenDetailHandler(index) {
      this.progressHistory = await Promise.all(
        this.progressHistory.map(async (item, itemIndex) => {
          if (itemIndex === index) {
            item.isOpen = index === itemIndex ? !item.isOpen : false;
            if (item.isOpen && !item.summary) {
              item.summary = (
                await Service.getIpHistory(
                  this.stipulationType,
                  this.selectedAcademicYear.id,
                  item.ipSequence
                )
              ).data;
            }
          }
          return item;
        })
      );
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/views/progress/shared/scss/common.scss';
</style>
